import FHIRUpdater from 'data/FHIRUpdater';
import type { Bundle, BundleEntry, CarePlan, CareTeam, Identifier, Patient, Reference } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import React from 'react';
import { List, Label, Loader, Popup, Icon } from 'semantic-ui-react';
import type { FlatCarePlan } from 'types';

import { useAuthContext } from '../../../../../contexts/AuthContext';
import { Referral, FHIRProxyClient } from '../../../../../fhir-sdoh';
import * as FHIRUtils from '../../../../../utils/FHIRUtils';
import Practitioners from '../../../../common/practitioners';
import type { StepMutate, StepPatientIdentity, StepPractitionerData } from '../../types';


/**
 * Props for {@link CareTeamCard}.
 *
 * `carePlan` is required rather than optional even though the shared
 * {@link StepCarePlanContext} leaves it open: `steps/index.tsx` renders this
 * child only inside `{carePlan && ...}`, and `updateCareTeam` reads
 * `flatCarePlan.careTeamId` unguarded, which only holds once a plan exists.
 */
type CareTeamCardProps = Omit<StepPatientIdentity, 'fhirPatient' | 'orgId' | 'patientId'> & {
  /** The payer org; passed straight into `Referral.updateCareTeam`. */
  orgId: string;
  /** The member's FHIR `Patient.id`; passed into `Referral.updateCareTeam`. */
  patientId: string;
  /**
   * The whole FHIR Patient resource. Required because `updateCareTeam` hands it
   * to `Referral.updateCareTeam` and to `updatePatientWithCTStatus`, neither of
   * which tolerates its absence.
   */
  fhirPatient: Patient;
  /** The member's CarePlan; its `careTeamId` links the team shown here. */
  carePlan: CarePlan;
  /** Fires after a role reassignment so the parent can refetch. */
  onUpdate: StepMutate;
  /**
   * Revalidates the `/CareTeam` query after a write.
   *
   * Required: `updateCareTeam` calls it as `props?.careTeamMutate()` — the `?.`
   * guards `props`, which is never absent, not the function itself.
   */
  careTeamMutate: StepMutate;
  /** The member's `/CareTeam` bundle; searched for the plan's team. */
  careTeamData?: Bundle | undefined;
  /**
   * The practitioner picker list, forwarded to `common/practitioners`.
   *
   * SUSPECTED DEFECT (type-level) — this is the shared `PractitionerEntry` from
   * `utils/bundleNarrowing`, whose `resource.id` is `string | undefined`
   * (FHIR gives `Practitioner.id` cardinality `0..1`). `common/practitioners`
   * declares its own local entry type with `id?: string`, which under
   * `exactOptionalPropertyTypes` refuses the explicit `undefined`. The two
   * describe the same runtime objects and disagree only about absence.
   * Reconciling them means editing `components/common`, outside this cluster.
   */
  PractitionerData?: StepPractitionerData;
};

function CareTeamCard(props: CareTeamCardProps) {
  const [loading, setLoading] = React.useState(false);
  const [popRDOpen, setpopRDOpen] = React.useState(false);
  const [popOAOpen, setpopOAOpen] = React.useState(false);
  const carePlan = props.carePlan;
  let flatCarePlan: FlatCarePlan | undefined;
  if (carePlan) {
    flatCarePlan = FHIRUtils.parseFHIRCarePlan(carePlan);
  }
  // SUSPECTED DEFECT — `BundleEntry.resource` is `0..1` in FHIR, so an entry
  // carrying no resource throws inside `.find` and the care-team panel takes the
  // careplan step down with it. Reported, not fixed; the `!` preserves that throw
  // rather than skipping the entry, which would silently pick a different team.
  //
  // The result is asserted to `CareTeam` because `/CareTeam/?subject=...` is a
  // single-type search and `parseFHIRCareTeam` requires it; filtering by
  // `resourceType` instead would add a runtime check that is not there today.
  //
  // The `?.` on `careteam` is a separate guard and is upstream's, not ours: it
  // covers a null/undefined ELEMENT of `entry`, where the predicate evaluates
  // false and `.find` moves on. Do not collapse it into the `!` — they guard
  // two different values, and removing it turns a skipped entry into a throw.
  const careteam = props?.careTeamData?.entry?.find((careteam: BundleEntry) => {
    return careteam?.resource!.id === flatCarePlan?.careTeamId;
  })?.resource as CareTeam | undefined; //to ensure the careteam gets dispalyed and updated are linked to the careplan

  const handleRDPopOpen = () => {
    setpopRDOpen(true);
  };
  const handleOAPopOpen = () => {
    setpopOAOpen(true);
  };

  const handleClose = () => {
    setpopOAOpen(false);
    setpopRDOpen(false);
  };

  const authInfo = useAuthContext();

  // SUSPECTED DEFECT — same shape as `steps/launch/index.tsx`:
  // `Patient.identifier` is `0..*` and read unguarded, so an identifier-less
  // Patient throws here *after* `Referral.updateCareTeam` has already written
  // the new assignment, leaving the care team updated and the patient's
  // care-team-status stale. Reported, not fixed; the `!` preserves the throw.
  function updatePatientWithCTStatus(fhirPatient: Patient, careTeamStatus: string) {
    const patientIdentifiers = fhirPatient.identifier!.map((identifier: Identifier) => {
      if (identifier.system === 'https://projectwell.io/fhir/identifiers/care-team-status') {
        identifier.value = careTeamStatus;
      }
      return identifier;
    });
    fhirPatient.identifier = patientIdentifiers;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
    FHIRUpdater('/Patient/' + fhirPatient.id, authInfo.token, fhirPatient);
  }

  /**
   * Takes the CareTeam `member` array — either the `fhirpath.evaluate` result for
   * the role, or the single-element array built from the newly selected
   * practitioner — and returns the bare id from the first entry's reference.
   */
  const getPractitionerId = (practitioner: Reference[]) => {
    // `split('/')[1]` is `string | undefined` under `noUncheckedIndexedAccess`,
    // but the `indexOf('/') !== -1` test above establishes the segment exists.
    let practitionerId: string | undefined | null = null;

    if (
      practitioner &&
      practitioner.length &&
      practitioner[0]?.reference &&
      practitioner[0]?.reference !== undefined &&
      practitioner[0]?.reference.indexOf('/') !== -1
    ) {
      practitionerId = practitioner[0].reference.split('/')[1];
    }
    return practitionerId;
  };

  const updateCareTeam = async (data: string, type: string) => {
    setpopOAOpen(false);
    setpopRDOpen(false);
    // Asserted, not defaulted: a default would swallow the throw a `null` `data`
    // currently raises on `.split`.
    const asignee = data.split('|')[0]!; //RD's or WA practionerId
    const selectedCareTeam = {
      reference: 'Practitioner/' + data.split('|')[0],
      display: data.split('|')[1],
    };

    // Each path selects `CareTeam.participant.member`, a FHIR `Reference` — so
    // the elements carry `reference`, all that `getPractitionerId` and
    // `Referral.updateCareTeam` read. `fhirpath.evaluate` returns `unknown[]`
    // (see `src/fhirpath.d.ts`), so the element type is stated at the boundary
    // rather than at each use.
    const currentOA = fhirpath.evaluate(
      careteam,
      "participant.where(role.coding.code='309396002').member" //TODO: Exrternalize codes
    ) as Reference[];

    const currentRD = fhirpath.evaluate(
      careteam,
      "participant.where(role.coding.code='159033005').member" //TODO: Exrternalize codes
    ) as Reference[];

    const currentCC = fhirpath.evaluate(
      careteam,
      "participant.where(role.coding.code='768820003').member" //TODO: Exrternalize codes
    ) as Reference[];

    const OAMember = type === 'OA' ? [selectedCareTeam] : currentOA;
    const RDMember = type === 'RD' ? [selectedCareTeam] : currentRD;
    const CCMember = type === 'CC' ? [selectedCareTeam] : currentCC;

    const OAPractitionerId = getPractitionerId(OAMember);
    const RDPractitionerId = getPractitionerId(RDMember);
    const _CCPractitionerId = getPractitionerId(CCMember);

    let status = 'UN-ASSIGNED';

    if (
      (OAPractitionerId !== null && OAPractitionerId !== import.meta.env.VITE_DEFAULT_PRACTITIONER_WA_ID) ||
      (RDPractitionerId !== null && RDPractitionerId !== import.meta.env.VITE_DEFAULT_PRACTITIONER_RD_ID)
    ) {
      status = 'ASSIGNED';
    }

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    setLoading(true);
    try {
      await Referral.updateCareTeam(
        props.fhirPatient,
        props.orgId,
        props.patientId,
        flatCarePlan!.careTeamId,
        OAMember,
        RDMember,
        CCMember,
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: authInfo.practitionerName,
        },
        authInfo.practitionerName,
        asignee,
        type,
        fhirProxyClient
      );
      updatePatientWithCTStatus(props.fhirPatient, status);
    } catch (error) {
      console.log('Error in updateOA():', (error instanceof Error ? error.message : String(error)));
      setLoading(false);
      throw error;
    }
    setLoading(false);
    props?.careTeamMutate();
    props.onUpdate(); //callback
  };

  return (
    <>
      {loading && <Loader active inline="centered" />}
      <List horizontal>
        {careteam && (
          <>
            <Popup
              flowing
              trigger={
                <Label as="a" color="blue" image>
                  {FHIRUtils.parseFHIRCareTeam(careteam).OA}
                  <Label.Detail as="a">
                    WA <Icon name="pencil alternate" color="teal" />
                  </Label.Detail>
                </Label>
              }
              content={
                <Practitioners
                  // SUSPECTED DEFECT — `org` and `type` are passed to a
                  // component that declares neither and reads neither.
                  // `Practitioners` shows every entry in `PractitionerData`, so
                  // the "OA,WA" / "RD" role filter these were meant to apply
                  // never happens: both pickers list the same practitioners.
                  // The filtering is done upstream in `careplan/index.tsx`
                  // (GUID-length only), not by role. Reported, not fixed —
                  // removing the attributes is inert, but making them work is a
                  // behaviour change.
                  PractitionerData={props.PractitionerData as React.ComponentProps<typeof Practitioners>['PractitionerData']}
                  org="PW"
                  type="OA,WA"
                  // SUSPECTED DEFECT — `Practitioners.onSelect` is declared
                  // `(selected: string | null) => void` and its own spec pins
                  // that `null` reaches here when the form is submitted without
                  // touching the dropdown. `updateCareTeam` then calls
                  // `data.split('|')` on it and throws. Reported, not fixed:
                  // guarding would silently swallow an empty submission, and
                  // whether that should no-op or fail is a product decision.
                  onSelect={(data: string | null) => updateCareTeam(data!, 'OA')}
                />
              }
              on="click"
              open={popOAOpen}
              onOpen={handleOAPopOpen}
              onClose={handleClose}
            />
            <Popup
              flowing
              trigger={
                <Label color="blue" as="a" image>
                  {FHIRUtils.parseFHIRCareTeam(careteam).RD}
                  <Label.Detail as="a">
                    RD <Icon name="pencil alternate" color="teal" />
                  </Label.Detail>
                </Label>
              }
              content={
                <Practitioners
                  PractitionerData={props.PractitionerData as React.ComponentProps<typeof Practitioners>['PractitionerData']}
                  org="PW"
                  type="RD"
                  // Same `string | null` defect as the OA picker above.
                  onSelect={(data: string | null) => updateCareTeam(data!, 'RD')}
                />
              }
              on="click"
              open={popRDOpen}
              onOpen={handleRDPopOpen}
              onClose={handleClose}
            />
          </>
        )}
      </List>
    </>
  );
}

export default CareTeamCard;
